import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

// Binary helpers
const hexToBytes = (hex) => {
  const clean = hex.toLowerCase();
  const len = clean.length;
  const out = new Uint8Array(len >> 1);
  for (let i = 0; i < len; i += 2) {
    out[i >> 1] = parseInt(clean.substr(i, 2), 16);
  }
  return out;
};

const bytesToHex = (bytes) => {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i].toString(16).padStart(2, "0");
    s += b;
  }
  return s;
};

// Varint (unsigned, 7-bit groups)
const writeVarUint = (num, arr) => {
  let n = Number(num >>> 0);
  while (n >= 0x80) {
    arr.push((n & 0x7f) | 0x80);
    n >>>= 7;
  }
  arr.push(n & 0x7f);
};

const readVarUint = (bytes, offset) => {
  let result = 0;
  let shift = 0;
  let pos = offset;
  while (pos < bytes.length) {
    const byte = bytes[pos++];
    result |= (byte & 0x7f) << shift;
    if ((byte & 0x80) === 0) break;
    shift += 7;
  }
  return { value: result >>> 0, next: pos };
};

// URL-safe Base64 for bytes
const bytesToBase64Url = (bytes) => {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const base64UrlToBytes = (str) => {
  try {
    if (!str || typeof str !== "string") {
      throw new Error("Invalid input: not a string");
    }
    const padLength = (4 - (str.length % 4)) % 4;
    const padded = str.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padLength);
    const bin = atob(padded);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch (error) {
    console.error("Base64 decode error:", error);
    throw new Error(`Failed to decode base64: ${error.message}`);
  }
};

// Payload pack: [version=1][flags][jobId varint][companyId? varint][refLen varint][refBytes]
// flags: bit0 => hasCompanyId
const packPayload = ({ jobId, refCode, companyId }) => {
  const out = [];
  // version
  out.push(1);
  // flags
  const hasCompany = companyId != null && companyId !== "";
  out.push(hasCompany ? 0x01 : 0x00);
  // numbers as varints
  writeVarUint(Number(jobId), out);
  if (hasCompany) writeVarUint(Number(companyId), out);
  // refCode: strip 'WBREF_' then hex->bytes if looks like hex
  let ref = String(refCode || "");
  if (ref.startsWith("WBREF_")) ref = ref.slice(6);
  let refBytes;
  if (/^[0-9a-fA-F]+$/.test(ref) && ref.length % 2 === 0) {
    refBytes = hexToBytes(ref);
  } else {
    // fallback to utf-8 bytes
    refBytes = new TextEncoder().encode(ref);
  }
  writeVarUint(refBytes.length, out);
  for (let i = 0; i < refBytes.length; i++) out.push(refBytes[i]);
  return new Uint8Array(out);
};

const unpackPayload = (bytes) => {
  let pos = 0;
  if (bytes.length < 2) return null;
  const version = bytes[pos++];
  if (version !== 1) return null;
  const flags = bytes[pos++];
  const hasCompany = (flags & 0x01) === 1;
  const j = readVarUint(bytes, pos);
  const jobId = j.value;
  pos = j.next;
  let companyId = undefined;
  if (hasCompany) {
    const c = readVarUint(bytes, pos);
    companyId = c.value;
    pos = c.next;
  }
  const rl = readVarUint(bytes, pos);
  const refLen = rl.value;
  pos = rl.next;
  if (pos + refLen > bytes.length) return null;
  const refBytes = bytes.slice(pos, pos + refLen);
  // Try to interpret as hex by default
  const refHex = bytesToHex(refBytes);
  return { jobId, companyId, refHex };
};

const ShortRedirect = () => {
  const { code } = useParams();

  useEffect(() => {
    if (!code) {
      window.location.href = "/";
      return;
    }

    try {
      const bytes = base64UrlToBytes(code);
      const unpacked = unpackPayload(bytes);
      
      if (!unpacked) {
        console.error("Failed to unpack payload");
        window.location.href = "/";
        return;
      }

      const { jobId, companyId, refHex } = unpacked;
      
      // Validate extracted values
      if (!jobId || isNaN(jobId) || jobId <= 0) {
        console.error("Invalid jobId:", jobId);
        window.location.href = "/";
        return;
      }

      const refCode = `WBREF_${refHex}`;
      const baseUrl = process.env.REACT_APP_WEB_URL || window.location.origin;
      
      // Construct URL with proper encoding
      let full;
      if (!companyId) {
        full = `${baseUrl}/job-lists-details/${jobId}/apply/${encodeURIComponent(refCode)}`;
      } else {
        full = `${baseUrl}/job-lists-details/${jobId}/apply/${encodeURIComponent(refCode)}/coy/${companyId}`;
      }

      // Validate URL before redirecting
      try {
        new URL(full);
        // Use href instead of replace for better browser compatibility
        window.location.href = full;
      } catch (urlError) {
        console.error("Invalid URL constructed:", full, urlError);
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Redirect error:", error);
      window.location.href = "/";
    }
  }, [code]);

  return (
    <div
      className="w-100 d-flex align-items-center justify-content-center"
      style={{ minHeight: 200 }}
    >
      <span>Redirecting…</span>
    </div>
  );
};

export default ShortRedirect;

// Also export helpers to be reused by the generator UI
export const encodeShortPayload = ({ jobId, refCode, companyId }) => {
  const bytes = packPayload({ jobId, refCode, companyId });
  return bytesToBase64Url(bytes);
};
